import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
} from '@angular/forms';

import { Subject, takeUntil } from 'rxjs';

import { TimeService, trackByProp } from '@recruitee/common';
import { InfoboxComponent } from '@recruitee/components/infobox';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import type { OpeningAssignment } from '@recruitee/offers-types';
import type { PlacementWithoutOpening } from '@recruitee/placements-types';
import type { RequisitionOpening } from '@recruitee/requisitions-types';
import { RequisitionOpeningReason } from '@recruitee/requisitions-types';
import {
  AvatarComponent,
  ButtonComponent,
  DialogBodyComponent,
  DialogComponentI,
  DialogData,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  HeadingComponent,
  IconComponent,
  InputComponent,
  SelectComponent,
  SelectOption,
  TruncateComponent,
} from '@recruitee/user-interface';
import { LifeCycle } from '@recruitee/utils';

type AssignHiresDialogData = {
  stepText: string;
  placements: PlacementWithoutOpening[];
  openings: RequisitionOpening[];
  assignments?: OpeningAssignment[];
  saveHandler?: (assignments: OpeningAssignment[]) => void;
  goBackHandler?: () => void;
};

type ControlsMap = { [key: number]: FormControl };
type OptionsMap = Map<number, SelectOption[]>;

function getOpeningsValidator(openingsCount: number): ValidatorFn {
  return (formGroup: FormGroup) => {
    const values: Array<number | null> = Object.values(formGroup.value);
    const allCandidatesAssignedToOpening = values.every(val => val);
    const allAvailableOpeningsSelected = values.filter(val => !!val).length === openingsCount;
    if (allCandidatesAssignedToOpening || allAvailableOpeningsSelected) return null;
    return { openingsNotAssigned: true };
  };
}

@Component({
  selector: 'rt-requisitions-assign-hires-dialog',
  standalone: true,
  imports: [
    AvatarComponent,
    ButtonComponent,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    HeadingComponent,
    IconComponent,
    InputComponent,
    SelectComponent,
    TruncateComponent,
    TranslatePipe,
    ReactiveFormsModule,
    InfoboxComponent,
  ],
  templateUrl: './assign-hires-dialog.component.html',
  styleUrls: ['./assign-hires-dialog.component.less'],
})
export class AssignHiresDialogComponent implements DialogComponentI<AssignHiresDialogData, any> {
  public trackById = trackByProp<{ id: number }>({ prop: 'id' });

  public mode: 'manual' | 'auto' = 'manual';

  public form: FormGroup;

  public stepText = '';
  public placements: PlacementWithoutOpening[] = [];
  public availableOpenings: RequisitionOpening[] = [];
  public openings: RequisitionOpening[] = [];
  public optionsMap: OptionsMap = new Map();
  public autoAssign = false;
  public notEnoughOpenings = false;

  public readonly texts = {
    openOn: (date: string) => this.i18n.translate('app.requisitions.openings.opens_on', { date }),
    selectOpening: this.i18n.translate(
      'app.requisitions.assign_hires_dialog.select_opening_placeholder',
    ),
    automaticallyAssigned: this.i18n.translate(
      'app.requisitions.assign_hires_dialog.automatically_assigned_label',
    ),
    notAssigned: this.i18n.translate('app.requisitions.assign_hires_dialog.not_assigned_label'),
    manuallyAssigned: this.i18n.translate(
      'app.requisitions.assign_hires_dialog.manually_assigned_label',
    ),
  };

  private readonly openingReasonText: Record<RequisitionOpeningReason, string> = {
    [RequisitionOpeningReason.NewPosition]: this.i18n.translate(
      'app.requisitions.openings.reason.new_position',
    ),
    [RequisitionOpeningReason.Replacement]: this.i18n.translate(
      'app.requisitions.openings.reason.replacement',
    ),
  };

  private labelsMap: Map<number, string> = new Map();

  @LifeCycle('onDestroy')
  private ngOnDestroy$: Subject<void>;

  constructor(
    private dialogRef: DialogRef,
    private cdRef: ChangeDetectorRef,
    private fb: FormBuilder,
    private i18n: I18nService,
    private timeService: TimeService,
    @Inject(DialogData) private data: AssignHiresDialogData,
  ) {}

  public setDialogData(data: AssignHiresDialogData, triggerUpdate: boolean): void {
    this.stepText = data.stepText;
    this.placements = data.placements;
    this.notEnoughOpenings = this.placements.length > data.openings.length;
    this.openings = this.sortOpenings(data.openings);
    this.availableOpenings = this.openings;
    const simpleOpenings: boolean = data.openings.some(
      opening => !opening.reason && !opening.anticipatedStartDate,
    );

    this.buildForm();
    this.buildLabels();
    this.buildOptions();

    if (simpleOpenings && !this.notEnoughOpenings) {
      this.autoAssign = true;
      this.autofill();
    } else if (data.assignments) {
      data.assignments.forEach(entry =>
        this.getOpeningControl(entry.placementId).patchValue(entry.openingId, { emitEvent: false }),
      );
      this.form.updateValueAndValidity();
    }
  }

  public getOpeningControl(placementId: number): FormControl {
    return <FormControl>this.form.get(`${placementId}`);
  }

  public goBack() {
    this.data.goBackHandler && this.data.goBackHandler();
    this.dialogRef.close();
  }

  public save() {
    const formValue: { [key: number]: number | null } = this.form.getRawValue();

    const assignments: OpeningAssignment[] = Object.entries(formValue).reduce(
      (acc, [key, value]) => {
        if (!value) return acc;

        acc.push({ placementId: parseInt(key, 10), openingId: value });
        return acc;
      },
      <OpeningAssignment[]>[],
    );

    this.data.saveHandler && this.data.saveHandler(assignments);
    this.dialogRef.close();
  }

  public cancel() {
    this.dialogRef.close();
  }

  private buildForm() {
    const controls = this.placements.reduce(
      (acc, placement) => ({
        ...acc,
        [placement.id]: new FormControl<number | string | null>(null),
      }),
      <ControlsMap>{},
    );
    this.form = new FormGroup(controls, getOpeningsValidator(this.openings.length));

    this.form.valueChanges.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
      const selectedOpenings = <number[]>(
        Object.values(this.form.getRawValue()).filter(value => !!value)
      );
      this.availableOpenings = this.openings.filter(
        opening => !selectedOpenings.includes(opening.id),
      );
      this.buildOptions();
      this.cdRef.detectChanges();
    });
  }

  private buildOptions() {
    this.optionsMap = new Map<number, SelectOption[]>();
    const selectedOpenings = this.form.getRawValue();

    this.placements.forEach(placement => {
      const options: SelectOption[] = [];
      const selectedOpeningId = selectedOpenings[placement.id];
      const selectedOpeningLabel = this.labelsMap.get(selectedOpeningId) || '';
      const openingsForOptions = this.openings.filter(
        opening => this.availableOpenings.includes(opening) || selectedOpeningId === opening.id,
      );
      const presentLabels = new Set<string>(options.map(option => option.label));

      openingsForOptions.forEach(opening => {
        const label = this.labelsMap.get(opening.id) || '';

        if (
          presentLabels.has(label) ||
          (label === selectedOpeningLabel && selectedOpeningId !== opening.id)
        )
          return;

        presentLabels.add(label);
        options.push({
          label,
          value: opening.id,
        });
      });

      this.optionsMap.set(placement.id, options);
    });
  }

  private buildLabels() {
    this.labelsMap = new Map<number, string>();
    this.openings.forEach(opening => {
      this.labelsMap.set(opening.id, this.getLabel(opening));
    });
  }

  private getLabel(opening: RequisitionOpening) {
    const reasonText = this.getReasonText(opening);
    const dateText = opening.anticipatedStartDate
      ? this.texts.openOn(
          this.timeService.format({
            input: opening.anticipatedStartDate,
            pattern: 'DD MMM YYYY',
          }),
        )
      : '';

    if (reasonText && dateText) return `${reasonText}: ${dateText}`;
    if (reasonText) return reasonText;
    if (dateText) return dateText;

    return this.notEnoughOpenings ? this.texts.manuallyAssigned : this.texts.automaticallyAssigned;
  }

  private autofill() {
    this.placements.forEach((placement, index) => {
      this.getOpeningControl(placement.id).patchValue(this.openings[index].id, {
        emitEvent: false,
      });
    });
    this.form.updateValueAndValidity();
  }

  private sortOpenings(opening: RequisitionOpening[]) {
    return opening.sort((opening1, opening2) => {
      if (
        opening1.reason &&
        opening2.reason &&
        opening1.anticipatedStartDate &&
        opening2.anticipatedStartDate
      ) {
        const opening1ReasonText = this.getReasonText(opening1);
        const opening2ReasonText = this.getReasonText(opening2);
        const comparisonResult = this.compareDates(
          opening1.anticipatedStartDate,
          opening2.anticipatedStartDate,
        );
        return comparisonResult === 0
          ? opening1ReasonText.localeCompare(opening2ReasonText)
          : comparisonResult;
      }

      if (opening1.anticipatedStartDate && opening2.anticipatedStartDate) {
        return this.compareDates(opening1.anticipatedStartDate, opening2.anticipatedStartDate);
      }

      return this.getLabel(opening1).localeCompare(this.getLabel(opening2));
    });
  }

  private compareDates(testDate: string, targetDate: string): number {
    return new Date(testDate).valueOf() - new Date(targetDate).valueOf();
  }

  private getReasonText(opening: RequisitionOpening): string {
    return opening.reason ? this.openingReasonText[opening.reason] : '';
  }
}
